/**
 * Location Selector Component
 * Handles the dropdown for selecting locations.
 * Uses IP geolocation to recommend and reorder the closest location when available.
 */

(function() {
  'use strict';

  const CONFIG = {
    toggleSelector: '[data-location-toggle]',
    dropdownSelector: '#locationDropdown',
    linkSelector: '[data-location]',
    activeClass: 'location-dropdown__item--active',
    recommendedClass: 'location-dropdown__item--recommended',
    geoApiUrl: 'https://ipapi.co/json/',
    geoTimeoutMs: 5000,
    storageKey: 'kraft_location_detected_v2'
  };

  /** LA metro area cities (ipapi often returns suburb, not "Los Angeles") */
  var LA_CITIES = 'los angeles,long beach,anaheim,santa ana,glendale,pasadena,torrance,pomona,fullerton,inglewood,el monte,downey,costa mesa,huntington beach,santa clarita,el monte,el cajon,simi valley,van nuys,norwalk,burbank,compton,south gate,carson,santa monica,whittier,alhambra,inglewood,west covina,newport beach,mission viejo,buena park,cypress,la habra,lake forest,westminster,garden grove,orange,fullerton,brea,placentia,yorba linda';
  /** Bay Area cities that map to San Francisco */
  var SF_CITIES = 'san francisco,oakland,berkeley,daly city,south san francisco,alameda,san leandro,richmond';
  /** South Bay / Silicon Valley → San Jose */
  var SAN_JOSE_CITIES = 'san jose,sunnyvale,santa clara,mountain view,palo alto,cupertino,milpitas,fremont,newark,union city,los altos,saratoga,campbell,gilroy';

  /**
   * Rough bounding boxes to distinguish SF proper vs South Bay (San Jose) when API city is wrong.
   * SF: ~37.70–37.83 lat, -122.52 to -122.35 lon
   * San Jose / South Bay: ~37.22–37.45 lat, -122.08 to -121.75 lon
   */
  function bayAreaFromCoords(lat, lon) {
    if (lat == null || lon == null || typeof lat !== 'number' || typeof lon !== 'number') return null;
    if (lat >= 37.65 && lon <= -122.35 && lon >= -122.55) return 'san-francisco';
    if (lat >= 37.2 && lat <= 37.5 && lon >= -122.15 && lon <= -121.7) return 'san-jose';
    if (lat >= 37.5 && lat <= 37.7 && lon <= -122.2) return 'san-francisco';
    return null;
  }

  /**
   * Map IP geo response (city, region, optional lat/lon) to our location handle.
   * Returns null if no match.
   */
  function mapGeoToHandle(city, region, lat, lon) {
    if (!city && !region && lat == null && lon == null) return null;
    var c = (city || '').toLowerCase().trim();
    var r = (region || '').toLowerCase().trim();
    if (c.length < 2 && r.length < 2 && lat == null && lon == null) return null;

    if (r === 'hawaii' || c.indexOf('honolulu') !== -1) return 'hawaii';
    if (r === 'new york' || c.indexOf('new york') !== -1 || c === 'brooklyn' || c === 'manhattan' || c === 'queens' || c === 'bronx' || c === 'staten island') return 'new-york';
    if (c === 'irvine') return 'irvine';
    if (LA_CITIES.indexOf(c) !== -1) return 'los-angeles';
    if (r.indexOf('orange') !== -1 && c !== 'irvine') return 'los-angeles';
    if (c.indexOf('los angeles') !== -1 || (r.indexOf('los angeles') !== -1)) return 'los-angeles';

    if (r === 'california' || r === 'ca') {
      var fromCoords = bayAreaFromCoords(lat, lon);
      if (fromCoords) return fromCoords;
    }
    if (c.indexOf('san francisco') !== -1 || SF_CITIES.indexOf(c) !== -1 || (r.indexOf('san francisco') !== -1 && SAN_JOSE_CITIES.indexOf(c) === -1)) return 'san-francisco';
    if (SAN_JOSE_CITIES.indexOf(c) !== -1 || c.indexOf('san jose') !== -1) return 'san-jose';

    return null;
  }

  /**
   * Fetch approximate location via IP (no user permission). Returns { city, region } or null.
   * Uses ipapi.co (CORS-friendly, HTTPS).
   */
  function fetchGeo() {
    var controller = new AbortController();
    var timeout = setTimeout(function() { controller.abort(); }, CONFIG.geoTimeoutMs);
    return fetch(CONFIG.geoApiUrl, { signal: controller.signal })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        clearTimeout(timeout);
        if (data && (data.city != null || (data.latitude != null && data.longitude != null))) {
          return {
            city: data.city,
            region: data.region,
            latitude: data.latitude != null ? Number(data.latitude) : null,
            longitude: data.longitude != null ? Number(data.longitude) : null
          };
        }
        return null;
      })
      .catch(function() {
        clearTimeout(timeout);
        return null;
      });
  }

  /**
   * Reorder dropdown: move the item with the given handle to the top and add "Closest location" label.
   */
  function applyRecommendedLocation(dropdown, handle) {
    var list = dropdown.querySelector('.location-dropdown__list');
    if (!list) return;

    var items = list.querySelectorAll('.location-dropdown__item');
    var targetItem = null;
    for (var i = 0; i < items.length; i++) {
      var link = items[i].querySelector(CONFIG.linkSelector);
      if (link && link.getAttribute('data-location') === handle) {
        targetItem = items[i];
        break;
      }
    }
    if (!targetItem) return;

    var link = targetItem.querySelector(CONFIG.linkSelector);
    if (!link) return;

    targetItem.classList.add(CONFIG.recommendedClass);
    var nameEl = link.querySelector('.location-dropdown__name');
    var label = document.createElement('span');
    label.className = 'location-dropdown__recommended-label';
    label.setAttribute('aria-hidden', 'true');
    label.textContent = 'Closest location';
    if (nameEl && nameEl.nextSibling) {
      link.insertBefore(label, nameEl.nextSibling);
    } else {
      link.appendChild(label);
    }

    list.insertBefore(targetItem, list.firstChild);
  }

  /**
   * Run geo detection, optionally use cache, then reorder and mark recommended.
   */
  function runLocationDetection(dropdown) {
    if (!dropdown) return;

    var cached = null;
    try {
      cached = sessionStorage.getItem(CONFIG.storageKey);
    } catch (e) {}

    function apply(handle) {
      if (handle) {
        try {
          sessionStorage.setItem(CONFIG.storageKey, handle);
        } catch (e) {}
        applyRecommendedLocation(dropdown, handle);
      }
    }

    if (cached) {
      apply(cached);
      return;
    }

    fetchGeo().then(function(geo) {
      if (!geo) return;
      var handle = mapGeoToHandle(geo.city, geo.region, geo.latitude, geo.longitude);
      apply(handle);
    });
  }

  // Initialize location selector
  function init() {
    const toggle = document.querySelector(CONFIG.toggleSelector);
    const dropdown = document.querySelector(CONFIG.dropdownSelector);
    
    if (!toggle || !dropdown) return;

    // Toggle dropdown
    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleDropdown(toggle, dropdown);
    });

    // Handle location selection
    const links = dropdown.querySelectorAll(CONFIG.linkSelector);
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        // Don't prevent default - let the link navigate
        handleLocationSelect(link, toggle, dropdown);
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!toggle.contains(e.target) && !dropdown.contains(e.target)) {
        closeDropdown(toggle, dropdown);
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && dropdown.getAttribute('aria-hidden') === 'false') {
        closeDropdown(toggle, dropdown);
        toggle.focus();
      }
    });

    // Location detection: reorder and recommend closest (async, non-blocking)
    runLocationDetection(dropdown);
  }

  // Toggle dropdown open/close
  function toggleDropdown(toggle, dropdown) {
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    
    if (isOpen) {
      closeDropdown(toggle, dropdown);
    } else {
      openDropdown(toggle, dropdown);
    }
  }

  // Open dropdown
  function openDropdown(toggle, dropdown) {
    toggle.setAttribute('aria-expanded', 'true');
    dropdown.setAttribute('aria-hidden', 'false');
  }

  // Close dropdown
  function closeDropdown(toggle, dropdown) {
    toggle.setAttribute('aria-expanded', 'false');
    dropdown.setAttribute('aria-hidden', 'true');
  }

  // Handle location selection
  function handleLocationSelect(link, toggle, dropdown) {
    const location = link.getAttribute('data-location');
    const locationName = link.querySelector('.location-dropdown__name')?.textContent || '';
    
    // Close dropdown (page will navigate via the link href)
    closeDropdown(toggle, dropdown);
    
    // Optional: Trigger custom event for analytics or other tracking
    const event = new CustomEvent('locationChange', {
      detail: { location, locationName }
    });
    document.dispatchEvent(event);
    
    // Note: No need to update UI - the link will navigate to the new page
    // and the correct active state will be shown on page load
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
